
import React, { useState, useEffect, useMemo } from 'react';
import SegmentProfile from './segment-components/SegmentProfile';
import SegmentPerformanceCharts from './segment-components/SegmentPerformanceCharts';
import StationPerformanceTable from './segment-components/StationPerformanceTable';
import SegmentDemographics from './segment-components/SegmentDemographics';
import SegmentDaypartAnalysis from './segment-components/SegmentDaypartAnalysis';
import AICampaignRecommendations from './AICampaignRecommendations';
import StationMapView from './segment-components/StationMapView';
import CustomerSegments from './segment-components/CustomerSegments';
import CustomerSatisfaction from './segment-components/CustomerSatisfaction';
import { Download, Users, MapPin, ChevronDown, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getSegmentColor, getSegmentName } from '../../utils/segmentUtils';

const SegmentDetail = ({ 
  segmentId = 'urban', 
  selectedStations = [], 
  filters = {}, 
  viewType = 'data', 
  timeFrame = 'monthly',
  categoryFilter = 'fuel',
  customerSatisfaction = {},
  clvData = {},
  demographicsData = [],
  onStationSelectionChange = () => {}, 
  onViewTypeChange = () => {},
  setTimeFrame = () => {},
  setCategoryFilter = () => {},
  onTabChange = () => {},
  onSegmentSelect = () => {},
  stationData = []
}) => {
  // State for dropdowns in the header
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // State for station selection UI
  const [stationSearchQuery, setStationSearchQuery] = useState('');
  const [selectedStationsList, setSelectedStationsList] = useState(selectedStations || []);
  const [favoriteStations, setFavoriteStations] = useState([]);
  const [recentStations, setRecentStations] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('volume');
  const [customerSegmentMetric, setCustomerSegmentMetric] = useState('volume');

  // References for the dropdown containers
  const dropdownRefs = {
    segment: React.useRef(null),
    stations: React.useRef(null)
  };

  // Segment options
  const segments = [
    { id: 'urban', name: 'Urban Premium', color: 'bg-purple-500' },
    { id: 'highway', name: 'Highway Transit', color: 'bg-blue-500' },
    { id: 'rural', name: 'Rural Standard', color: 'bg-green-500' },
    { id: 'suburban', name: 'Suburban Mixed', color: 'bg-amber-500' }
  ];

  // Find the selected segment
  const selectedSegmentObj = segments.find(s => s.id === segmentId) || segments[0];

  // Toggle dropdown open/close
  const toggleDropdown = (dropdown) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdown);
    }
  };

  // Handle segment selection
  const handleSegmentSelect = (segmentId) => {
    onSegmentSelect(segmentId);
    setOpenDropdown(null);
    toast.success(`Selected ${segments.find(s => s.id === segmentId).name} segment`);
  };
  
  // Handle going back to overview
  const handleBackToOverview = () => {
    onTabChange('overview');
    toast.success('Returned to Segment Overview');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && 
          dropdownRefs[openDropdown]?.current && 
          !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Ensure demographicsData is always an array with fallback values
  const defaultDemographics = [
    { name: 'Local Residential', value: 30 },
    { name: 'Commercial', value: 45 },
    { name: 'Commuters', value: 25 }
  ];
  
  const safeDemographicsData = Array.isArray(demographicsData) && demographicsData.length > 0 
    ? demographicsData 
    : defaultDemographics;

  // Update local state when props change
  useEffect(() => {
    setSelectedStationsList(selectedStations || []);
  }, [selectedStations]);

  // Sample station data
  const allStations = useMemo(() => [
    { id: 'URB001', name: 'Central Bangkok', region: 'bangkok', format: 'flagship', volume: 5200, premium: 42, margin: 0.92, cafe: 320, traffic: 847, basketSize: 752, clv: 9.1, status: 'above', loyalty: 47, carcare: 145, repeat: 53, lat: 13.7563, lng: 100.5018 },
    { id: 'URB002', name: 'Silom Junction', region: 'bangkok', format: 'standard', volume: 4800, premium: 38, margin: 0.88, cafe: 290, traffic: 820, basketSize: 720, clv: 8.7, status: 'above', loyalty: 43, carcare: 130, repeat: 48, lat: 13.7247, lng: 100.5284 },
    { id: 'URB003', name: 'Sukhumvit 63', region: 'bangkok', format: 'flagship', volume: 4600, premium: 44, margin: 0.90, cafe: 310, traffic: 830, basketSize: 765, clv: 9.0, status: 'above', loyalty: 45, carcare: 150, repeat: 51, lat: 13.7326, lng: 100.5691 },
    { id: 'URB004', name: 'Ratchada Station', region: 'bangkok', format: 'standard', volume: 4300, premium: 36, margin: 0.86, cafe: 280, traffic: 790, basketSize: 695, clv: 8.2, status: 'below', loyalty: 40, carcare: 110, repeat: 42, lat: 13.7639, lng: 100.5619 },
    { id: 'URB005', name: 'Pattaya Main', region: 'eastern', format: 'flagship', volume: 4700, premium: 40, margin: 0.89, cafe: 300, traffic: 815, basketSize: 730, clv: 8.9, status: 'above', loyalty: 44, carcare: 135, repeat: 46, lat: 12.9277, lng: 100.8765 },
    { id: 'HWY001', name: 'Highway 7 (North)', region: 'central', format: 'standard', volume: 5800, premium: 32, margin: 0.82, cafe: 210, traffic: 925, basketSize: 690, clv: 7.8, status: 'below', loyalty: 36, carcare: 95, repeat: 35, lat: 14.0583, lng: 100.6367 },
    { id: 'HWY002', name: 'Highway 9 (East)', region: 'eastern', format: 'standard', volume: 5500, premium: 28, margin: 0.80, cafe: 190, traffic: 890, basketSize: 670, clv: 7.5, status: 'below', loyalty: 34, carcare: 90, repeat: 33, lat: 13.9114, lng: 100.7178 },
    { id: 'RUR001', name: 'Chiang Mai North', region: 'northern', format: 'standard', volume: 3200, premium: 26, margin: 0.84, cafe: 160, traffic: 625, basketSize: 580, clv: 7.2, status: 'above', loyalty: 49, carcare: 65, repeat: 56, lat: 18.7953, lng: 98.9864 },
    { id: 'RUR002', name: 'Khon Kaen Central', region: 'northern', format: 'standard', volume: 2900, premium: 24, margin: 0.85, cafe: 150, traffic: 590, basketSize: 560, clv: 6.9, status: 'below', loyalty: 47, carcare: 60, repeat: 52, lat: 16.4322, lng: 102.8236 },
    { id: 'SUB001', name: 'Pathum Thani', region: 'central', format: 'standard', volume: 3600, premium: 30, margin: 0.83, cafe: 210, traffic: 725, basketSize: 650, clv: 7.6, status: 'above', loyalty: 38, carcare: 85, repeat: 41, lat: 14.0208, lng: 100.5251 },
    { id: 'SUB002', name: 'Nonthaburi Central', region: 'central', format: 'flagship', volume: 3800, premium: 34, margin: 0.87, cafe: 230, traffic: 760, basketSize: 680, clv: 8.0, status: 'above', loyalty: 41, carcare: 95, repeat: 44, lat: 13.8622, lng: 100.5142 },
  ], []);

  // Filter stations based on segment and search query
  const filteredStations = useMemo(() => {
    const segmentPrefix = {
      'urban': 'URB',
      'highway': 'HWY',
      'rural': 'RUR',
      'suburban': 'SUB'
    }[segmentId] || '';
    
    return allStations
      .filter(station => station.id.startsWith(segmentPrefix))
      .filter(station => 
        station.name.toLowerCase().includes(stationSearchQuery.toLowerCase()) ||
        station.id.toLowerCase().includes(stationSearchQuery.toLowerCase())
      );
  }, [allStations, segmentId, stationSearchQuery]);

  // Load sample favorites from localStorage
  useEffect(() => {
    const storedFavorites = localStorage.getItem('favoriteStations');
    if (storedFavorites) {
      setFavoriteStations(JSON.parse(storedFavorites));
    }
    
    const storedRecent = localStorage.getItem('recentStations');
    if (storedRecent) {
      setRecentStations(JSON.parse(storedRecent));
    } else {
      // Sample recent stations if none in localStorage
      setRecentStations(['URB001', 'HWY001', 'SUB001']);
    }
  }, []);

  // Sample trend data based on segment
  const trendData = useMemo(() => [
    { month: 'Jan', urban: 105, highway: 98, rural: 89, suburban: 92, urbanCafe: 110, highwayCafe: 95, ruralCafe: 85, suburbanCafe: 90 },
    { month: 'Feb', urban: 107, highway: 95, rural: 91, suburban: 93, urbanCafe: 112, highwayCafe: 92, ruralCafe: 88, suburbanCafe: 93 },
    { month: 'Mar', urban: 109, highway: 93, rural: 93, suburban: 94, urbanCafe: 115, highwayCafe: 90, ruralCafe: 92, suburbanCafe: 95 },
    { month: 'Apr', urban: 108, highway: 91, rural: 94, suburban: 93, urbanCafe: 113, highwayCafe: 89, ruralCafe: 94, suburbanCafe: 94 },
    { month: 'May', urban: 110, highway: 89, rural: 96, suburban: 92, urbanCafe: 118, highwayCafe: 87, ruralCafe: 96, suburbanCafe: 93 },
    { month: 'Jun', urban: 112, highway: 87, rural: 97, suburban: 91, urbanCafe: 120, highwayCafe: 85, ruralCafe: 98, suburbanCafe: 92 },
  ], []);
  
  // Define the segment data
  const segmentData = useMemo(() => ({
    urban: {
      name: 'Urban Premium',
      margin: 0.89,
      nfrSales: 47,
      cafeVolume: 245,
      loyalty: 43,
      clv: clvData.urban || 8.7,
      targetCLV: 9.0,
      traffic: 847,
      basketSize: 752,
      conversionRate: 68,
      peakHours: '7-9 AM',
      satisfaction: customerSatisfaction.urban || 92
    },
    highway: {
      name: 'Highway Transit',
      margin: 0.76,
      nfrSales: 38,
      cafeVolume: 215,
      loyalty: 32,
      clv: clvData.highway || 6.4,
      targetCLV: 8.0,
      traffic: 925,
      basketSize: 820,
      conversionRate: 52,
      peakHours: '10-2 PM',
      satisfaction: customerSatisfaction.highway || 85
    },
    rural: {
      name: 'Rural Standard',
      margin: 0.85,
      nfrSales: 28,
      cafeVolume: 170,
      loyalty: 47,
      clv: clvData.rural || 5.3,
      targetCLV: 7.0,
      traffic: 625,
      basketSize: 580,
      conversionRate: 58,
      peakHours: '5-7 PM',
      satisfaction: customerSatisfaction.rural || 89
    },
    suburban: {
      name: 'Suburban Mixed',
      margin: 0.81,
      nfrSales: 33,
      cafeVolume: 195,
      loyalty: 38,
      clv: clvData.suburban || 7.1,
      targetCLV: 8.0,
      traffic: 725,
      basketSize: 650,
      conversionRate: 62,
      peakHours: '8-10 AM',
      satisfaction: customerSatisfaction.suburban || 87
    }
  })[segmentId] || {
    name: 'Urban Premium',
    margin: 0.89,
    nfrSales: 47,
    cafeVolume: 245,
    loyalty: 43,
    clv: 8.7,
    targetCLV: 9.0,
    traffic: 847,
    basketSize: 752,
    conversionRate: 68,
    peakHours: '7-9 AM',
    satisfaction: 92
  }, [segmentId, customerSatisfaction, clvData]);

  // Handle station selection
  const handleStationSelect = (stationId) => {
    setSelectedStationsList(prev => {
      const isSelected = prev.includes(stationId);
      
      if (isSelected) {
        return prev.filter(id => id !== stationId);
      } else {
        // Add to recent stations if not already there
        if (!recentStations.includes(stationId)) {
          const newRecent = [stationId, ...recentStations].slice(0, 5);
          setRecentStations(newRecent);
          localStorage.setItem('recentStations', JSON.stringify(newRecent));
        }
        
        return [...prev, stationId];
      }
    });
  };

  // Function to select/deselect all stations
  const handleSelectAllStations = () => {
    setSelectedStationsList(filteredStations.map(station => station.id));
  };

  const handleClearAllStations = () => {
    setSelectedStationsList([]);
  };

  // Function to toggle favorite status for a station
  const handleToggleFavorite = (stationId) => {
    setFavoriteStations(prev => {
      const isFavorite = prev.includes(stationId);
      
      let newFavorites;
      if (isFavorite) {
        newFavorites = prev.filter(id => id !== stationId);
      } else {
        newFavorites = [...prev, stationId];
      }
      
      localStorage.setItem('favoriteStations', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Update the parent component when stations change
  useEffect(() => {
    onStationSelectionChange(selectedStationsList);
  }, [selectedStationsList, onStationSelectionChange]);

  // Download report handler
  const handleDownloadReport = () => {
    toast.success('Downloading report...');
  };

  return (
    <div className="bg-gray-50">
      {/* Enhanced Back Button with clear visual cue */}
      <div className="mb-6">
        <button 
          onClick={handleBackToOverview}
          className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-purple-700 hover:text-purple-900 hover:bg-gray-50 font-medium shadow-sm transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Segment Overview
        </button>
      </div>

      {/* Header Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-medium text-gray-900 mb-1">
              {segmentData.name || 'Urban Premium'} Segment Analysis
            </h2>
            <p className="text-gray-500">
              {segmentId === 'urban' ? 523 : 
               segmentId === 'highway' ? 682 : 
               segmentId === 'rural' ? 754 : 384} stations across network with average volume of {
                segmentId === 'urban' ? '4,300' : 
                segmentId === 'highway' ? '5,800' : 
                segmentId === 'rural' ? '2,900' : '3,600'
              } L/day
            </p>
          </div>
          
          {/* Moved segment and stations selectors to here */}
          <div className="flex items-center space-x-3">
            {/* Segment selector */}
            <div ref={dropdownRefs.segment} className="relative">
              <button
                className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm hover:border-purple-400 transition-colors"
                onClick={() => toggleDropdown('segment')}
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${selectedSegmentObj.color}`}></div>
                  <span className="font-medium text-sm">{selectedSegmentObj.name}</span>
                </div>
                <ChevronDown size={16} className={`ml-2 transition-transform duration-200 ${openDropdown === 'segment' ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Segment dropdown menu */}
              {openDropdown === 'segment' && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-48">
                  <div className="py-1">
                    {segments.map((segment) => (
                      <button
                        key={segment.id}
                        className={`w-full text-left px-4 py-2 flex items-center hover:bg-gray-50 ${
                          segment.id === segmentId ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                        }`}
                        onClick={() => handleSegmentSelect(segment.id)}
                      >
                        <div className={`w-3 h-3 rounded-full mr-2 ${segment.color}`}></div>
                        <span className="text-sm">{segment.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Stations selector */}
            <div ref={dropdownRefs.stations} className="relative">
              <button
                className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm hover:border-purple-400 transition-colors"
                onClick={() => toggleDropdown('stations')}
              >
                <div className="flex items-center">
                  <MapPin size={16} className="text-purple-600 mr-2" />
                  <span className="font-medium text-sm">
                    {selectedStationsList.length > 0 
                      ? `${selectedStationsList.length} Station${selectedStationsList.length > 1 ? 's' : ''}`
                      : 'All Stations'}
                  </span>
                </div>
                <ChevronDown size={16} className={`ml-2 transition-transform duration-200 ${openDropdown === 'stations' ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Stations dropdown */}
              {openDropdown === 'stations' && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-72">
                  <div className="p-3">
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Search stations..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={stationSearchQuery}
                        onChange={(e) => setStationSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    <div className="flex justify-between mb-2">
                      <button 
                        className="text-xs text-purple-600 hover:text-purple-800"
                        onClick={handleSelectAllStations}
                      >
                        Select All
                      </button>
                      <button 
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={handleClearAllStations}
                      >
                        Clear All
                      </button>
                    </div>
                    
                    <div className="max-h-48 overflow-y-auto">
                      {filteredStations.length > 0 ? (
                        filteredStations.map(station => (
                          <div 
                            key={station.id}
                            className="flex items-center py-1.5 px-2 hover:bg-gray-50 rounded-md cursor-pointer"
                            onClick={() => handleStationSelect(station.id)}
                          >
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={selectedStationsList.includes(station.id)}
                              onChange={() => {}}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{station.name}</p>
                              <p className="text-xs text-gray-500">{station.id}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No stations found</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Download/Export button */}
            <button 
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
              onClick={handleDownloadReport}
            >
              <Download size={16} className="mr-2" />
              Export Data
            </button>
          </div>
        </div>
      </div>
      
      {/* Segment Profile Cards */}
      <SegmentProfile 
        selectedSegment={segmentData} 
        includeCustomerSatisfaction={true}
      />
      
      {viewType === 'data' ? (
        <SegmentPerformanceCharts 
          segmentId={segmentId}
          trendData={trendData}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          categoryFilter={categoryFilter}
          timeFrame={timeFrame}
          setTimeFrame={setTimeFrame}
          setCategoryFilter={setCategoryFilter}
        />
      ) : (
        <StationMapView 
          segmentId={segmentId}
          stations={filteredStations.filter(station => 
            selectedStationsList.length === 0 || selectedStationsList.includes(station.id)
          )}
        />
      )}
      
      <StationPerformanceTable 
        stationData={filteredStations.filter(station => 
          selectedStationsList.length === 0 || selectedStationsList.includes(station.id)
        )}
        favoriteStations={favoriteStations}
        handleToggleFavorite={handleToggleFavorite}
        includeCarCare={true}
        includeRepeatCustomers={true}
        customerSatisfaction={customerSatisfaction} // Pass the customer satisfaction data
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <CustomerSegments 
          segmentId={segmentId} 
          selectedMetric={customerSegmentMetric}
          setSelectedMetric={setCustomerSegmentMetric}
        />
        <SegmentDemographics 
          segmentId={segmentId} 
          demographicsData={safeDemographicsData}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <CustomerSatisfaction segmentId={segmentId} satisfactionData={customerSatisfaction} />
        <SegmentDaypartAnalysis segmentId={segmentId} />
      </div>
      
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center text-lg font-medium text-gray-900">
            <Users size={20} className="mr-2 text-purple-600" />
            AI-Powered Campaign Recommendations
          </div>
        </div>
        <div className="p-4">
          <AICampaignRecommendations 
            segmentId={segmentId} 
            selectedStations={selectedStationsList}
          />
        </div>
      </div>
    </div>
  );
};

export default SegmentDetail;
